import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useComplaintPrefill, useCreateComplaint, useAddComplaintSource } from '../../hooks/useComplaints';
import { ChoiceButton, ErrorLine, textDir } from '../guestReviews/shared';
import { CATEGORIES, CATEGORY_LABELS, SEVERITIES, SEVERITY_LABELS, TEXT, errorText } from '../../config/complaintLabels';
import '../../styles/GuestReviewsPage.css';
import '../../styles/ComplaintsPage.css';

/**
 * «فتح شكوى» — ONE button and ONE form behind all three ways in (tasks/complaints-to-fixes.md C1):
 *   the inbox side panel  → `{ kind: 'message', id: <conversation id> }`
 *   a flagged reply card  → `{ kind: 'review',  id: <reply id> }`
 *   the booking page      → `{ kind: 'booking', id: <booking id> }`
 *   a «صوت الضيف» row     → `{ kind: 'ota_review', id: <review id>, topic: <topic key> }` — the complaint keeps the
 *                           topic, so the tab can tell when the problem comes back after the fix (guest-voice G7, G13)
 *
 * The server decides everything the form starts from — the booking, the units of the stay (a choice only when
 * the guest was moved), the guest's words, and this stay's open complaints, so the same problem is ATTACHED
 * rather than opened twice (C6). Nothing is typed twice: the guest's words travel with the source. Core staff
 * only — every caller renders it only for them, and the API refuses everyone else.
 */
// A complaint lives on the «الشكاوى» tab of «التقييمات والشكاوى» (C19); `id` opens it there.
const complaintLink = (id) => `/ops/reviews?list=complaints&id=${id}`;

const OpenComplaintForm = ({ source, onHide, onDone }) => {
  const { data, isLoading, isError, error: loadError } = useComplaintPrefill(source, true);
  const create = useCreateComplaint();
  const attach = useAddComplaintSource();
  const [unitId, setUnitId] = useState(null);
  const [category, setCategory] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const busy = create.isPending || attach.isPending;
  // Attaching a booking-page source adds nothing — there are no guest words behind it.
  const canAttach = source.kind !== 'booking';

  useEffect(() => {
    if (data && unitId === null) setUnitId(String(data.defaultUnitId));
  }, [data, unitId]);
  // A review's AI check suggests a category and severity (tasks/review-complaint-triage.md T13) — a
  // starting point only: ops can change both, and must still write the description.
  useEffect(() => {
    if (!data || !data.suggested) return;
    setCategory(c => c || data.suggested.category);
    setSeverity(v => v || data.suggested.severity);
  }, [data]);

  const act = async (work) => {
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(errorText(err));
    }
  };
  const submit = () => act(async () => {
    const res = await create.mutateAsync({ source, propertyId: unitId, category, severity, description, topicKey: source.topic || null });
    onDone({ id: res.data.id, attached: false });
  });
  const attachTo = (id) => act(async () => {
    await attach.mutateAsync({ id, source });
    onDone({ id, attached: true });
  });

  // A conversation can raise more than one problem — one complaint each (C5). A source already on a
  // complaint is said so, and a new, different problem can still be opened from it.
  const canOpen = Boolean(data);

  return (
    <Modal show onHide={onHide} size="lg" centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title>{TEXT.modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && <div className="gr-state"><Spinner animation="border" size="sm" /></div>}
        {isError && <div className="gr-error" role="alert">{errorText(loadError)}</div>}

        {data && (
          <>
            <div className="gr-details">
              {data.booking.guestName && <span dir="auto">{data.booking.guestName}</span>}
              {data.booking.code && <span>{TEXT.booking} <bdi>{data.booking.code}</bdi></span>}
            </div>
            {data.booking.cancelled && <div className="gr-notice">{TEXT.cancelledBooking}</div>}
            {data.linkedTo && (
              <div className="gr-notice">
                {/* The link may land on this very page (a reply card sits on it) — so it closes the form too. */}
                {TEXT.linked} <Link to={complaintLink(data.linkedTo)} onClick={onHide}>{TEXT.view}</Link>
              </div>
            )}

            {data.source.excerpt && (
              <section className="gr-block">
                <h3 className="gr-block-title">{TEXT.sourcesTitle}</h3>
                <blockquote className="gr-text" dir="auto">{data.source.excerpt}</blockquote>
              </section>
            )}

            {canOpen && data.openComplaints.length > 0 && (
              <section className="gr-block">
                <h3 className="gr-block-title">{TEXT.existingTitle}</h3>
                {canAttach && <p className="gr-hint">{TEXT.existingHint}</p>}
                <ul className="cp-existing">
                  {data.openComplaints.map(c => (
                    <li key={c.id}>
                      <span className="cp-category">{CATEGORY_LABELS[c.category] || c.category}</span>
                      <span className="cp-existing-desc" dir="auto">{c.description}</span>
                      {c.unit && <span className="gr-muted">{c.unit.name}</span>}
                      {String(c.id) === String(data.linkedTo) && <span className="gr-muted">{TEXT.alreadyHere}</span>}
                      {canAttach && String(c.id) !== String(data.linkedTo) ? (
                        <Button variant="outline-secondary" className="touch-btn touch-btn-sm" onClick={() => attachTo(c.id)} disabled={busy}>
                          {TEXT.attach}
                        </Button>
                      ) : (
                        <Link to={complaintLink(c.id)} onClick={onHide}>{TEXT.view}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {canOpen && (
              <section className="gr-block">
                <h3 className="gr-block-title">{TEXT.newComplaint}</h3>

                {data.units.length > 1 && (
                  <>
                    <p className="cp-label">{TEXT.unitChoice}</p>
                    <p className="gr-hint">{TEXT.guestMoved}</p>
                    <div className="gr-choices" role="group" aria-label={TEXT.unitChoice}>
                      {data.units.map(u => (
                        <ChoiceButton key={u.id} active={unitId === String(u.id)} disabled={busy} onClick={() => setUnitId(String(u.id))}>
                          {u.name} {u.code && <bdi>({u.code})</bdi>}
                        </ChoiceButton>
                      ))}
                    </div>
                  </>
                )}

                <p className="cp-label">{TEXT.category}</p>
                <div className="gr-choices" role="group" aria-label={TEXT.category}>
                  {CATEGORIES.map(code => (
                    <ChoiceButton key={code} active={category === code} disabled={busy} onClick={() => setCategory(code)}>
                      {CATEGORY_LABELS[code]}
                    </ChoiceButton>
                  ))}
                </div>

                <p className="cp-label">{TEXT.severity}</p>
                <div className="gr-choices" role="group" aria-label={TEXT.severity}>
                  {SEVERITIES.map(code => (
                    <ChoiceButton key={code} active={severity === code} disabled={busy} onClick={() => setSeverity(code)}>
                      {SEVERITY_LABELS[code]}
                    </ChoiceButton>
                  ))}
                </div>
                <p className="gr-hint">{TEXT.severityHint}</p>
                {data.suggested && <p className="gr-hint">{TEXT.suggestedHint}</p>}

                <Form.Control
                  as="textarea"
                  rows={3}
                  className="touch-input gr-textarea mt-2"
                  value={description}
                  maxLength={1000}
                  disabled={busy}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={TEXT.descriptionPlaceholder}
                  aria-label={TEXT.descriptionPlaceholder}
                  dir={textDir(description)}
                />
                {data.departed && <p className="gr-hint">{TEXT.departedHint}</p>}
              </section>
            )}

            <ErrorLine message={error} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="touch-btn" onClick={onHide} disabled={busy}>{TEXT.cancel}</Button>
        {canOpen && (
          <Button className="touch-btn gr-primary" onClick={submit} disabled={busy || !category || !severity || !description.trim()}>
            {create.isPending ? <Spinner animation="border" size="sm" /> : TEXT.submit}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const OpenComplaintButton = ({ source, variant = 'outline-secondary' }) => {
  const [show, setShow] = useState(false);
  const [done, setDone] = useState(null);

  return (
    <>
      <Button
        variant={variant}
        className={`touch-btn cp-open-btn ${variant === 'link' ? 'gr-link' : ''}`}
        onClick={() => { setDone(null); setShow(true); }}
      >
        <i className="bi bi-exclamation-diamond" aria-hidden="true"></i> {TEXT.openComplaint}
      </Button>
      {done && (
        <span className="cp-done" role="status">
          {done.attached ? TEXT.attached : TEXT.created} <Link to={complaintLink(done.id)}>{TEXT.viewComplaint}</Link>
        </span>
      )}
      {show && (
        <OpenComplaintForm
          source={source}
          onHide={() => setShow(false)}
          onDone={(result) => { setDone(result); setShow(false); }}
        />
      )}
    </>
  );
};

export default OpenComplaintButton;
